import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Coffee, ArrowRight, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function AdminResetPassword() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) {
            toast.error("Missing reset token. Request a new link.");
        }
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) { toast.error("Use letters and at least one number."); return; }
        if (password !== confirm) { toast.error("Passwords don't match."); return; }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            setTimeout(() => navigate("/admin/login"), 2200);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Couldn't reset. Try requesting a new link.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-cream-light" data-testid="admin-reset-password">
            <div className="hidden md:flex md:w-1/2 text-cream p-12 flex-col justify-between relative overflow-hidden" style={{ backgroundColor: "#1a0f00" }}>
                <div className="grain-light" />
                <div className="relative z-10">
                    <div className="tracked text-gold text-[10px]">CAFE HIDEOUT · CMS</div>
                </div>
                <div className="relative z-10">
                    <Coffee size={48} strokeWidth={1.1} className="text-gold mb-6" />
                    <h1 className="font-editorial font-bold text-cream text-5xl lg:text-6xl leading-[0.95]">
                        Pick a new one. <br /> Keep it private.
                    </h1>
                    <p className="font-body text-cream/70 mt-6 max-w-md text-base">
                        Eight characters minimum. Letters and a number, please.
                    </p>
                </div>
                <div className="relative z-10 font-mono text-[10px] text-cream/40">
                    © {new Date().getFullYear()} CAFE HIDEOUT
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-8 md:p-12">
                <div className="w-full max-w-md">
                    {done ? (
                        <div data-testid="reset-success">
                            <CheckCircle2 size={36} strokeWidth={1.2} className="text-gold mb-6" />
                            <div className="tracked text-coffee/50 text-[10px] mb-4">All Set</div>
                            <h2 className="font-editorial font-bold text-coffee text-4xl md:text-5xl leading-tight">
                                Fresh password.
                            </h2>
                            <p className="font-body text-coffee/70 mt-5 leading-relaxed">
                                Your password has been updated. Redirecting you to sign in…
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={submit} data-testid="reset-form">
                            <div className="tracked text-coffee/50 text-[10px] mb-4">Reset Password</div>
                            <h2 className="font-editorial font-bold text-coffee text-4xl md:text-5xl leading-tight">
                                Set a new key.
                            </h2>
                            <p className="font-body text-coffee/60 mt-3 mb-10">
                                Choose something memorable but tough to guess.
                            </p>

                            <div className="space-y-7">
                                <div>
                                    <label className="tracked text-coffee/60 text-[10px] block mb-3">New Password</label>
                                    <div className="relative">
                                        <input
                                            type={showPwd ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            data-testid="reset-password"
                                            className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 pr-10 text-coffee font-body outline-none"
                                            required
                                            minLength={8}
                                        />
                                        <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-0 top-3 text-coffee/60" aria-label="toggle password visibility">
                                            {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="tracked text-coffee/60 text-[10px] block mb-3">Confirm Password</label>
                                    <input
                                        type={showPwd ? "text" : "password"}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        data-testid="reset-confirm"
                                        className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none"
                                        required
                                        minLength={8}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !token}
                                className="btn-pill btn-pill-coffee mt-10 w-full justify-center disabled:opacity-50"
                                data-testid="reset-submit"
                            >
                                {loading ? "Setting…" : "Set New Password"} <ArrowRight size={14} />
                            </button>

                            <Link to="/admin/login" className="mt-8 inline-block tracked text-[10px] text-coffee/60 hover:text-gold transition-colors" data-testid="reset-back-login">
                                Back to Sign In
                            </Link>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
